import type { ContextKind } from "@pilotfish/core";
import { type FormEvent, useEffect, useId, useRef, useState } from "react";
import type { FactSubject, MemoryFact } from "../api/memory";
import type { RosterMember } from "../api/roster";
import { memberLabel } from "../members";

/**
 * Saving a durable fact — the same form for adding one and for correcting one (issue #302).
 *
 * ## The kind comes from the section, and is never asked
 *
 * There is no type picker here and there must not be. A family adds under *"What you're
 * working toward"* because that is the heading they were reading; hiding the five-way taxonomy
 * is most of what Boone is for, and a form with a `contextType` field hands it straight back.
 * So {@link kind} is a prop, passed by whichever section the control was pressed in, and the
 * word `GOAL` appears nowhere a family can see.
 *
 * ## Whose it is *is* asked, and that is not the same thing
 *
 * `remember` requires a `subject` and refuses to default one (#92): *"the family's"* is a
 * claim, not a cautious fallback, and a member's own goal stored as the household's becomes
 * something the whole group is told it committed to. That is a question about people, which a
 * family understands, rather than about schema — so it is asked, in their words, with no
 * pre-selection on a new fact.
 *
 * A fact stored before #92 can come back `unstated`, which is not a value this form can send:
 * the tool takes `family` or `member` and nothing else. So editing one requires answering, and
 * that is the *"that's mine, not ours"* repair #92 exists to enable, arriving at the one
 * surface where somebody can see the fact while they answer.
 *
 * ## The name is edited here, like everything else
 *
 * `deriveSlug` turns the title into the sort key (ADR 0003 decision 7), so changing a name
 * moves the row while changing a sentence does not — and for two releases that storage fact
 * reached the family: first as a read-only name telling them to add-and-delete (#265), then as
 * a separate *Rename* button beside *Edit*. Neither is something a person should have to know.
 *
 * `correct_this` (#83, #306) is addressed by the fact rather than by its title, so one write
 * carries whatever moved. This form is one form again, and Save is one confirmation whether
 * they changed the name, the wording, whose it is, or all three.
 */
export function FactForm({
  kind,
  fact,
  members,
  pending,
  locked,
  error,
  onSubmit,
  onCancel,
}: {
  kind: ContextKind;
  /** The fact being corrected, or `undefined` when this is a new one. */
  fact?: MemoryFact;
  members: readonly RosterMember[];
  pending: boolean;
  /**
   * A confirmation is open on the page behind this form (PR #330 review).
   *
   * **Separate from {@link pending}, and the gap between them is the bug it closes.**
   * `pending` is false while the dialog waits for an answer — `send`'s `finally` runs as soon
   * as the proposal lands — so *Save it* stayed live behind the scrim, and Shift+Tab out of
   * the dialog reached it. `Confirm`'s Tab cycle is the other half of that mitigation, not
   * the whole of it, which is what `Memory.tsx` disabling every list control already
   * recognises; this form was the one control it did not reach. `InviteForm` takes the same
   * prop for the same reason.
   */
  locked: boolean;
  error: string | null;
  onSubmit: (values: { title: string; content: string; subject: FactSubject }) => void;
  onCancel: () => void;
}) {
  const editing = fact !== undefined;
  const [title, setTitle] = useState(fact?.title ?? "");
  const [content, setContent] = useState(fact?.content ?? "");
  // `""` is "nobody has said yet" and is not submittable — the required select is what makes
  // an `unstated` fact answer the question rather than inherit an assumption.
  const [subject, setSubject] = useState<string>(initialSubject(fact, members));

  const titleId = useId();
  const contentId = useId();
  const subjectId = useId();
  const firstFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  /**
   * Opening the form moves focus into it.
   *
   * The trigger unmounts when the form appears, so a keyboard user was dumped to `<body>` and
   * had to Tab through the header and every section above to reach the first field. `Menu.tsx`
   * already restores focus to its trigger for the same reason.
   */
  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  const trimmedTitle = title.trim();
  const trimmedContent = content.trim();
  /**
   * **Refuse, never truncate** (#119).
   *
   * `maxLength` on a `<textarea>` clips a paste with no feedback: the form stays valid, the
   * write applies, and the family's own words are silently shortened — which is the exact
   * corruption that rule names, since `saveFact` sends this string as `rawText` and ADR 0014
   * decision 4's accountability rests on it being verbatim. So the bound is checked rather than
   * enforced by the input, and the count appears before somebody reaches it.
   */
  const tooLong = trimmedContent.length > CONTENT_LIMIT;
  const ready = trimmedTitle.length > 0 && trimmedContent.length > 0 && subject !== "" && !tooLong;
  // Nothing in this form may start a second write, or edit the body of one that is already
  // being confirmed, while the dialog is open.
  const frozen = pending || locked;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready || frozen) return;
    onSubmit({
      title: trimmedTitle,
      content: trimmedContent,
      subject: subject === "family" ? { kind: "family" } : { kind: "member", memberId: subject },
    });
  }

  return (
    <form className="factform" onSubmit={submit}>
      {/* The canvas titles the add form by the kind it will save under — *"Something else to
          work around"* — which is the same information the heading above carries, said as what
          this form is about to do. It is not repeated when correcting: the kind of a fact that
          already exists is not in question, and the row it sits inside is the subject. */}
      {editing ? null : <h3 className="factform__title">{ANOTHER[kind]}</h3>}

      <div className="field">
        <label className="field__label" htmlFor={titleId}>
          {editing ? "What it's called" : "Give it a short name"}
        </label>
        <input
          className="field__input"
          id={titleId}
          ref={firstFieldRef as React.RefObject<HTMLInputElement>}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          disabled={frozen}
          /* The rule #265 bought, in the one place somebody is about to break it. */
          placeholder="What it's about — not what it currently says"
          /* Matches `INPUT_LIMITS.title` exactly. Capping an <input> is safe where capping the
             textarea is not: a title is a short label somebody types, not a pasted passage. */
          maxLength={TITLE_LIMIT}
          autoComplete="off"
        />
        <p className="field__hint">
          Keep amounts, dates and numbers out of the name — those go below, so they can be changed
          later. “Lake house budget”, not “Budget ~$350k”.
        </p>
      </div>

      <div className="field">
        <label className="field__label" htmlFor={contentId}>
          What should Boone remember?
        </label>
        <textarea
          className="field__input field__input--area"
          id={contentId}
          ref={editing ? (firstFieldRef as React.RefObject<HTMLTextAreaElement>) : undefined}
          value={content}
          onChange={(event) => setContent(event.target.value)}
          disabled={frozen}
          rows={3}
        />
        {trimmedContent.length < CONTENT_LIMIT - 400 ? null : (
          <p className="field__hint" role={tooLong ? "alert" : undefined}>
            {tooLong
              ? `That's ${trimmedContent.length.toLocaleString()} characters — ${(trimmedContent.length - CONTENT_LIMIT).toLocaleString()} too many to save. Shorten it and nothing will be lost.`
              : `${trimmedContent.length.toLocaleString()} of ${CONTENT_LIMIT.toLocaleString()} characters.`}
          </p>
        )}
      </div>

      <div className="field">
        <label className="field__label" htmlFor={subjectId}>
          Whose is this?
        </label>
        <select
          className="field__input"
          id={subjectId}
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          disabled={frozen}
        >
          <option value="">Choose…</option>
          <option value="family">The whole family</option>
          {members.map((member) => (
            <option key={member.userId} value={member.userId}>
              {memberLabel(member, members)}
            </option>
          ))}
        </select>
      </div>

      {error === null ? null : (
        <p className="factform__error" role="alert">
          {error}
        </p>
      )}

      <div className="factform__actions">
        <button className="cta" type="submit" disabled={!ready || frozen}>
          {pending ? "Saving…" : editing ? "Save the change" : "Save it"}
        </button>
        <button className="cta cta--quiet" type="button" onClick={onCancel} disabled={frozen}>
          Cancel
        </button>
        {/* **Where the kind came from, said once, at the moment it is settled.** Nobody is ever
            asked to choose between "goal" and "preference" — hiding that taxonomy is most of
            what Boone is for — so the one place it can quietly go wrong is somebody pressing
            *Add* under a heading they were not reading. The canvas answers that here rather
            than with a picker: it names the outcome and its cause in the same breath. */}
        {editing ? null : <span className="factform__because">{KEPT_AS[kind]}</span>}
      </div>
    </form>
  );
}

/**
 * What the add form calls itself, per kind.
 *
 * `Memory.dc.html` writes only the `CONSTRAINT` one (*"Something else to work around"*); the
 * other four are that sentence in the same voice, which is `CONTEXT_KIND_HEADING`'s voice —
 * an answer rather than a category name.
 *
 * **Local to this app, and typed as a total `Record` so a sixth kind will not compile.** It is
 * not in `@pilotfish/core` because core's rule is *words more than one runtime must agree on*,
 * and these are spoken by one screen: `show_what_you_know` has no add control to title. The
 * headings and the nouns are shared precisely because both surfaces say them.
 */
const ANOTHER: Readonly<Record<ContextKind, string>> = {
  GOAL: "Something else you're working toward",
  CONSTRAINT: "Something else to work around",
  PREFERENCE: "Something else about how you like things done",
  DECISION: "Something else you've decided",
  FACT: "Something else to keep in mind",
};

/** The canvas's helper line, per kind — *"…because that's the list you added it to."* */
const KEPT_AS: Readonly<Record<ContextKind, string>> = {
  GOAL: "Boone keeps this as something you're working toward, because that's the list you added it to.",
  CONSTRAINT:
    "Boone keeps this as something to work around, because that's the list you added it to.",
  PREFERENCE:
    "Boone keeps this as how you like things done, because that's the list you added it to.",
  DECISION:
    "Boone keeps this as something you've decided, because that's the list you added it to.",
  FACT: "Boone keeps this as something to keep in mind, because that's the list you added it to.",
};

/** `INPUT_LIMITS.title` and `INPUT_LIMITS.contextContent`, which the tool enforces server-side. */
const TITLE_LIMIT = 200;
const CONTENT_LIMIT = 4_000;

/**
 * What the "whose is this" select starts on.
 *
 * A new fact starts on nothing, so somebody has to say. An existing one starts on what the
 * record holds — except `unstated`, which starts on nothing for the same reason: it is the
 * absence #92 was about, and pre-selecting *"the whole family"* would resolve it by assumption
 * on the screen built to let a family resolve it on purpose.
 *
 * **A departed member is the third case, and it used to look like the second.** `fetchRoster`
 * is active-only (ADR 0019 decision 5 keeps a departed member's attribution on the record), so
 * their id matches no `<option>` — and a controlled `<select>` whose value matches nothing
 * renders `selectedIndex === -1`, which is *blank*, not "Choose…". `subject !== ""` was still
 * true, so the form looked unanswered and submitted anyway, earning a refusal from #117's
 * member check. Narrowing the initial value to what the roster can actually offer drops it to
 * "Choose…", which reads as *this needs an answer* — true, and what the row above already says
 * in words ("Someone who has left").
 */
function initialSubject(fact: MemoryFact | undefined, members: readonly RosterMember[]): string {
  if (fact === undefined) return "";
  if (fact.subject.kind === "family") return "family";
  const id = fact.subjectMemberId;
  return id !== null && members.some((member) => member.userId === id) ? id : "";
}
